import React from 'react';
import { Link } from 'react-router-dom';
import { t } from '../lib/i18n';
import { openConsent } from './CookieBanner';
import { Facebook, Instagram, Mail, Phone, MapPin, ExternalLink, Send } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-black border-t border-[#262626] text-white mt-12" data-testid="footer">
      {/* Facebook CTA Section */}
      <div className="bg-[#141414] py-10">
        <div className="max-w-[1200px] mx-auto px-4 text-center">
          <h3 className="text-xl font-bold mb-2 text-white">{t('Ne ratez aucune offre — suivez-nous sur Facebook')}</h3>
          <p className="text-[#808080] text-sm mb-6">{t('Rejoignez la communauté DealRun !')}</p>
          <a
            href="https://www.facebook.com/109017284043222"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#E50914] text-white rounded-sm font-bold text-sm hover:bg-[#F40612] transition-all active:scale-95 shadow-lg shadow-[#E50914]/20"
            data-testid="facebook-follow-btn"
          >
            <Facebook size={20} />
            {t('Suivre DealRun')}
            <ExternalLink size={14} />
          </a>
        </div>
      </div>

      {/* Footer Content */}
      <div className="max-w-[1200px] mx-auto px-4 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* About */}
          <div>
            <h4 className="mb-4">
              <img src="/logo-dealrun.png" alt="DealRun" className="h-9 w-auto logo-dark" />
              <img src="/logo-dealrun-light.png" alt="DealRun" className="h-9 w-auto logo-light" />
            </h4>
            <p className="text-[#808080] text-sm leading-relaxed">
              Catalogues et offres des plus grandes enseignes de La Réunion, réunis en un seul endroit !
              Parcourez, planifiez vos achats et économisez avec DealRun
            </p>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider mb-4 text-[#B3B3B3]">{t('Catégories populaires')}</h4>
            <ul className="space-y-2">
              {[
                ['supermarche-hypermarche', 'Supermarchés / Hypermarchés'],
                ['brico-deco', 'Bricolage / Décoration'],
                ['ameublement-literie-cuisine', 'Ameublement / Literie / Cuisine'],
                ['mode-chaussures-lingerie', 'Mode / Chaussures / Lingerie'],
                ['high-tech-mobile', 'High-tech / Mobile'],
                ['electromenager-tv', 'Électroménager / TV'],
              ].map(([slug, cat]) => (
                <li key={slug}>
                  <Link to="/" state={{ category: slug }} className="text-[#808080] text-sm hover:text-[#E50914] transition-colors" data-testid={`footer-category-${slug}`}>
                    {cat}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider mb-4 text-[#B3B3B3]">{t('Contact')}</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-[#808080] text-sm">
                <MapPin size={16} className="flex-shrink-0" />
                {t('La Réunion, France')}
              </li>
              <li className="flex items-center gap-3 text-[#808080] text-sm">
                <Mail size={16} className="flex-shrink-0" />
                <a href="mailto:contact@dealrun.re" className="hover:text-white">contact@dealrun.re</a>
              </li>
              <li className="flex items-center gap-3 text-[#808080] text-sm">
                <Phone size={16} className="flex-shrink-0" />
                <a href="tel:+262693463628" className="hover:text-white" dir="ltr">+262 693 46 36 28</a>
              </li>
            </ul>
            <Link to="/contact" className="inline-flex items-center gap-2 mt-5 h-9 px-4 rounded-full border border-[#262626] text-xs font-bold text-white hover:border-[#E50914] hover:text-[#E50914] transition-colors" data-testid="footer-contact-btn"><Send size={13} className="rtl:-scale-x-100" /> {t('Nous contacter')}</Link>
            <div className="flex items-center gap-3 mt-5">
              <a
                href="https://www.facebook.com/109017284043222"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#E50914] transition-all hover:scale-110"
              >
                <Facebook size={18} />
              </a>
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#E50914] transition-all hover:scale-110"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#262626] mt-8 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#808080] text-xs">
            {t('© 2026 DealRun — Tous droits réservés')} · SARL DEALRUN
          </p>
          <div className="flex items-center gap-4">
            <Link to="/legal/legal-notice" className="text-[#808080] text-xs hover:text-white transition-colors" data-testid="footer-legal-notice">{t('Mentions légales')}</Link>
            <Link to="/legal/terms" className="text-[#808080] text-xs hover:text-white transition-colors" data-testid="footer-terms">{t('Conditions d’utilisation')}</Link>
            <Link to="/legal/privacy" className="text-[#808080] text-xs hover:text-white transition-colors" data-testid="footer-privacy">{t('Politique de confidentialité')}</Link>
            <Link to="/legal/cookies" className="text-[#808080] text-xs hover:text-white transition-colors" data-testid="footer-cookies">{t('Cookies')}</Link>
            <Link to="/contact" className="text-[#808080] text-xs hover:text-white transition-colors" data-testid="footer-contact">{t('Contact')}</Link>
            <Link to="/annonceur" className="text-[#808080] text-xs hover:text-white transition-colors" data-testid="footer-advertiser">{t('Espace annonceur')}</Link>
            <button type="button" onClick={openConsent} className="text-[#808080] text-xs hover:text-white transition-colors" data-testid="footer-cookie-settings">{t('Paramètres des cookies')}</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
